import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import ProviderBase from "components/layout/ProviderBase";
import ProviderPayoutReportTable from "../widgets/ProviderPayoutReportTable";
import * as ProviderApi from "api/ProviderApi";
import { getProviders } from "api/ProviderApi";
import getProviderId from "utils/getProviderId";
import { getNodeId } from "utils/AdminData";
import filter from "lodash/filter";
import flatMap from "lodash/flatMap";
import includes from "lodash/includes";
import map from "lodash/map";
import {
  PermissionTypes,
  userHasPermission,
} from "utils/permissions/rolesPermissions";
import * as UserPermissions from "constants/UserPermissions";
import Dropdown from "components/common/Dropdown";
import Button from "components/common/Button";
import TextBox from "components/common/TextBox";
import { NHO } from "constants/OrgNodeTypes";
import { handleErrorResponse } from "utils/request";
import { BOEHRINGER_INGELHEIM } from "constants/ProviderIds";
import styles from "./ProviderPayoutReport.scss";

function ProviderPayoutReport(props) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, index) => currentYear - index);
  const recentYears = map(years, y => ({ value: y, name: y }));
  const [checkSummary, setCheckSummary] = useState(null);
  const [dropDownValue, setDropDownValue] = useState(BOEHRINGER_INGELHEIM);
  const [checkNumber, setCheckNumber] = useState(null);
  const [downloadParameters, setDownloadParameters] = useState({});
  const [manufacturers, setManufacturers] = useState(null);
  const [ddlYear, setDdlYear] = useState(currentYear);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
      setLoading(true); 

      getProviders()
      .then((res) => {
          const mans = filter(res.body, { visibleInCorporateDashboard: true });
          const ddOptions = map(mans, item => ({
              value: item.id,
              name: item.name
            }));
          setManufacturers(ddOptions);
          setDropDownValue(BOEHRINGER_INGELHEIM);
      })
      .catch((err) => {
          handleErrorResponse("loading Manufacturers", err);
      })
      .finally(() => {
          setLoading(false);
      })
    };
  
    useEffect(() => {
      if (!manufacturers?.length) {
        fetchData();
      }
      }, []);

  const checkNumberInputStyle = {
    marginTop: "0.5rem",
  };

  const loadPayoutReport =  (model) => {

    if(props.node?.nodeType === NHO) {
      ProviderApi.getNHOProviderCheckSummary(checkNumber, ddlYear)
      .then((res) => {
        let recs = map(res.body, (d) => ({ ...d, id: d.clinicId }));
        setCheckSummary(recs);
      })
      .catch((error) => {
        handleErrorResponse("loading payout report", error);
      })
    } else {
      ProviderApi.getProviderCheckSummary(model)
      .then((res) => {
        let recs = map(res.body, (d) => ({ ...d, id: d.clinicId }));
        setCheckSummary(recs);
      })
      .catch((error) => {
        handleErrorResponse("loading payout report", error);
      })
    };
  };

  const handleStatusFilterChanged = (option) => {
    setDropDownValue(option.value);
  };

  const handleCheckInput = (number) => {
    setCheckNumber(number.value);
  };

  const handleYearChanged = (option) => {
    setDdlYear(option.value);
  };

  const handleSubmit = () => {
    let search = ({
      manufacturerId: dropDownValue,
      checkNumber: checkNumber,
      redemptionYear: ddlYear,
      corporateGroupProviderId: props.providerId,
    });
    loadPayoutReport(search);
  };

  useEffect(() => {
    if(props.node?.nodeType === NHO) {
      setDownloadParameters({ "checkNumber": checkNumber, "redemptionYear": ddlYear });
    } else {
      setDownloadParameters({
        "manufacturerId": dropDownValue,
        "checkNumber": checkNumber,
        "redemptionYear": ddlYear,
        "corporateGroupProviderId": props.providerId,
      })
    }
  }, [dropDownValue, checkNumber, props.providerId])

  return (
    <ProviderBase
      location={props.location}
      nodeId={props.nodeId}
      providerId={props.providerId}
      docTitle={"Payout Report"}
      pageTitle={"Reports"}
      collapsible
    >
      <div>
        <div className="flex flex-wrap">
          <div className="flex-none flex flex-centered spaced-content margin-sm">
            <Dropdown
              label="&nbsp;"
              name="ddlProvider"
              wide
              placeHolder={"Choose a Manufacturer"}
              value={dropDownValue}
              onChange={handleStatusFilterChanged}
              options={manufacturers}
              disabled={props.node?.nodeType === NHO}
            />
          </div>
          <div className="flex-none flex flex-centered spaced-content margin-sm">
            <Dropdown
              label="Redemption Year"
              name="ddlYear"
              wide
              placeHolder={"Choose Redemption Year"}
              value={ddlYear}
              onChange={handleYearChanged}
              options={recentYears}
            />
          </div>
          <div className="flex-none flex flex-centered spaced-content margin-sm">
            <TextBox
              label="&nbsp;"
              name="checkNumber"
              placeholder="Enter Check Number..."
              style={checkNumberInputStyle}
              value={checkNumber}
              onChange={(data) => handleCheckInput(data)}
              input={true}
            />
          </div>
          <div className="flex-none flex flex-centered flex-column spaced-content margin-sm">
            <div style={{ height: "38px" }}></div>
            <Button
              className="margin-right-x-sm"
              onClick={() => handleSubmit()}
              disabled={!checkNumber}
            >
              Search
            </Button>
          </div>
        </div>
          <ProviderPayoutReportTable
            isAdminReport={false}
            checkData={checkSummary}
            downloadParameters={downloadParameters}
            isNHO={props.node?.nodeType === NHO}
          ></ProviderPayoutReportTable>
      </div>
    </ProviderBase>
  );
}

export default connect(
  (state, ownProps) => {
    const params = ownProps?.match?.params || {};
    const userProfile = state.user.userProfile;
    const providerId = getProviderId(params, userProfile);
    const nodeId = getNodeId(state, ownProps);
    const node = state.entities.nodes?.[nodeId];
    const corporateGroupProviderIds = flatMap(filter(state.entities.providers, { isCorporateGroup: true }), "id");
    const isCorporateGroup = !!includes(corporateGroupProviderIds, node?.providerId);
    //Permissions
    const canViewCorporateGroupClinic = userHasPermission(PermissionTypes.VIEW, UserPermissions.CORPORATE_GROUP_CLINIC, userProfile);

    return {
      userProfile,
      providerId,
      nodeId,
      node,
      isCorporateGroup,
      isAdmin: !!(
        ownProps?.location?.pathname?.includes("/admin") &&
        providerId &&
        nodeId
      ),
      territoryId: params.territoryId,
      districtId: params.districtId,
      regionId: params.regionId,
      isLoggedIn: state.user.isLoggedIn,

      // Permissions
      canViewCorporateGroupClinic,
    };
  },
  (dispatch) => ({
    loadCurrentUser: () => dispatch(UserActions.loadCurrentUser()),
  })
)(ProviderPayoutReport);
